import React, { useContext, useMemo } from 'react';
import {
  Checkbox,
  FormControlLabel,
  Grid
} from '@material-ui/core';
import { useUserPreferences, useUpdateUserPreferences } from '../../../hooks';
import { LanguageString } from '../../Common';
import { SiteContext, UserContext } from '../../../contexts';
import * as DTO from '../../../dto';

export interface CaseLoadColumnSettings {
  name: boolean;
  twoMonthsAgoMTSStatus: boolean;
  lastMonthMTSStatus: boolean;
  currentMonthStatus: boolean;
  coachMeeting: boolean;
  lastMTSInteraction: boolean;
  lastRoadmapInteraction: boolean;
  customProperties: { [key: string]: boolean };
}

export const initialColumnsSettings: CaseLoadColumnSettings = {
  name: true,
  twoMonthsAgoMTSStatus: true,
  lastMonthMTSStatus: true,
  currentMonthStatus: true,
  coachMeeting: true,
  lastMTSInteraction: true,
  lastRoadmapInteraction: true,
  customProperties: {}
};

export const CaseLoadColumnSettingsTab: React.FC = () => {
  const site = useContext(SiteContext);
  const userCtx = useContext(UserContext);
  const userPreferences = useUserPreferences({ variables: { userId: userCtx.user.id, keys: [DTO.PreferenceKey.CaseLoadColumn] } });
  const [updatePrefs] = useUpdateUserPreferences();
  console.log(`site custom props: `, site.site?.caseLoadSettings?.customPropertyColumns);
  const customPropertyColumns = React.useMemo(() => {
    return site.site?.caseLoadSettings?.customPropertyColumns?.split(',') || [];
  }, [site.site?.caseLoadSettings?.customPropertyColumns]);

  const columns = useMemo(() => {
    if (userPreferences.data?.userPreference.length && userPreferences.data.userPreference[0].value) {
      return JSON.parse(userPreferences.data.userPreference[0].value);
    }

    return initialColumnsSettings;
  }, [userPreferences.data]);

  const setColumns = async (c: CaseLoadColumnSettings) => {
    await updatePrefs({
      variables: {
        input: [{ key: DTO.PreferenceKey.CaseLoadColumn, value: JSON.stringify(c) }]
      },
      refetchQueries: ['GetUserPreferences']
    });
  };

  return (
    <Grid item xs>
      <Grid container>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={columns.name}
                value="primary"
                onChange={async event => {
                  const c = columns;
                  c.name = event.target.checked;
                  await setColumns(c);
                }}
              />
            }
            label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMN_NAME" defaultText="Participant Name" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={columns.twoMonthsAgoMTSStatus}
                value="primary"
                onChange={async event => {
                  const c = columns;
                  c.twoMonthsAgoMTSStatus = event.target.checked;
                  await setColumns(c);
                }}
              />
            }
            label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMN_TWO_MONTHS_STATUS" defaultText="Two Months Ago MTS" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={columns.lastMonthMTSStatus}
                value="primary"
                onChange={async event => {
                  const c = columns;
                  c.lastMonthMTSStatus = event.target.checked;
                  await setColumns(c);
                }}
              />
            }
            label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMN_LAST_MONTH_STATUS" defaultText="Last Month MTS" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={columns.currentMonthStatus}
                value="primary"
                onChange={async event => {
                  const c = columns;
                  c.currentMonthStatus = event.target.checked;
                  await setColumns(c);
                }}
              />
            }
            label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMN_CURRENT_MONTH_STATUS" defaultText="Current Month MTS" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={columns.coachMeeting}
                value="primary"
                onChange={async event => {
                  const c = columns;
                  c.coachMeeting = event.target.checked;
                  await setColumns(c);
                }}
              />
            }
            label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMN_COACH_MEETING" defaultText="Coach Meeting" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={columns.lastMTSInteraction}
                value="primary"
                onChange={async event => {
                  const c = columns;
                  c.lastMTSInteraction = event.target.checked;
                  await setColumns(c);
                }}
              />
            }
            label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMN_LAST_MTS_INTERACTION" defaultText="Last MTS Interaction" />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={columns.lastRoadmapInteraction}
                value="primary"
                onChange={async event => {
                  const c = columns;
                  c.lastRoadmapInteraction = event.target.checked;
                  await setColumns(c);
                }}
              />
            }
            label={<LanguageString groupName="CASE_LOAD" resourceName="COLUMN_LAST_ROADMAP_INTERACTION" defaultText="Last Roadmap Interaction" />}
          />
        </Grid>
        {customPropertyColumns.map((customPropertyColumn, index) => (
          <Grid item xs={12} md={4} key={`customprop_column_${index}`}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={columns.customProperties[customPropertyColumn]}
                  value="primary"
                  onChange={async event => {
                    const c = columns;
                    c.customProperties[customPropertyColumn] = event.target.checked;
                    await setColumns(c);
                  }}
                />
              }
              label={
                <LanguageString groupName='CASE_LOAD' resourceName={customPropertyColumn} defaultText={customPropertyColumn} />
              }
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

export default CaseLoadColumnSettingsTab;
